import { useEffect, useState } from 'react';
import {Link, useParams} from 'react-router-dom';
import axios from 'axios';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  TextField,
  Typography
} from '@mui/material';
import { deleteVendor, getVendor, patchVendor } from '../../api/vendorsAPI';
import { formatPhoneNum } from '../../utils/phoneUtils';

import NavBar from '../layout/NavBar';
import ServiceModal from "../modals/ServiceModal";

export default function ViewVendor() {
  // Get ID of vendor from URL
  let { id } = useParams();

  // Vendor information from fetch call
  const [vendorInfo, setVendorInfo] = useState([]);
  const [vendorName, setVendorName] = useState('');
  const [vendorCode, setVendorCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [vendorNotes, setVendorNotes] = useState('');
  const [editVendorErrors, setEditVendorErrors] = useState({});

  // Track modal state for the edit user modal
  const [openUser, setOpenUser] = useState(false);
  const handleOpenUser = () => setOpenUser(true);
  const handleCloseUser = () => setOpenUser(false);

  // Track modal state for the new vehicle modal
  const [openVehicle, setOpenVehicle] = useState(false);
  const handleOpenVehicle = () => setOpenVehicle(true);
  const handleCloseVehicle = () => setOpenVehicle(false);

  // Track modal state for vehicle linking modal
  const [openVehicleLink, setOpenVehicleLink] = useState(false);
  const handleOpenVehicleLink = () => setOpenVehicleLink(true);
  const handleCloseVehicleLink = () => setOpenVehicleLink(false);

  // Track modal state for vehicle unlinking modal
  const [openVehicleUnlink, setOpenVehicleUnlink] = useState(false);
  const handleOpenVehicleUnlink = () => setOpenVehicleUnlink(true);
  const handleCloseVehicleUnlink = () => setOpenVehicleUnlink(false);

  // Track vehicle that has the add service button clicked
  const [targetVehicle, setTargetVehicle] = useState(null);

  // Track modal state for the add service modal
  const [openService, setOpenService] = useState(false);
  const handleOpenService = (vehicleId) => {
    setTargetVehicle(vehicleId);
    setOpenService(true);
  }
  const handleCloseService = () => setOpenService(false);

  // Set empty vehicle form values for adding a new vehicle
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [color, setColor] = useState('');
  const [license, setLicense] = useState(null);
  const [vin, setVin] = useState(null);
  const [notes, setNotes] = useState('');

  // Hold all vehicles for linking purposes
  const [vehicles, setVehicles] = useState([]);

  // Hold vendor's vehicles for unlinking purposes
  const [vendorVehicles, setVendorVehicles] = useState([]);

  // Holds the vehicle that is attempting to be linked to a vendor
  const [vehicleLink, setVehicleLink] = useState('');

  function makeVehicleDescription(vehicle) {
    let id = vehicle.id;
    let color = vehicle.color ? vehicle.color : '';
    let year = vehicle.year ? vehicle.year : ''
    let make = vehicle.make ? vehicle.make : '';
    let model = vehicle.model ? vehicle.model : '';
    let license = vehicle.license ? vehicle.license : '';
    let otherOwners = vehicle.list_owners_str ? '| ' + vehicle.list_owners_str : '| No vendors associated';
    return `(ID: ${id}) ${color} ${year} ${make} ${model} ${license} ${otherOwners}`
  }

  /** Make GET request using ID from URL param to grab vendor data */
  const getVendorInfo = async () => {
    try {
      const response = await getVendor(id);
      const vendorData = response.data;

      setVendorInfo(vendorData);
      setVendorName(vendorData.vendor_name || '');
      setVendorCode(vendorData.vendor_code || '');
      setPhoneNumber(vendorData.phone || '');
      setVendorEmail(vendorData.email || '');
      setWebsite(vendorData.website || '');
      setVendorNotes(vendorData.notes || '');

      vendorData.vehicles && setVendorVehicles(vendorData.vehicles.map(vehicle => makeVehicleDescription(vehicle)));
    } catch (error) {
      console.error(error);
    }
  }

  const handleVendorDelete = async () => {
    try {
      const confirmation = window.confirm('Are you sure you want to delete this vendor?\nThis action cannot be undone.');
      if (!confirmation) {
        return;
      }

      await deleteVendor(id);
      window.location = process.env.REACT_APP_BASE_URL + 'vendors';
    } catch (error) {
      console.error(error);
    }
  };

  /** Make PATCH request to update vendor data */
  const handleUpdateVendor = async () => {
    let values = {
      'vendor_name': vendorName,
      'vendor_code': vendorCode,
      'phone': phoneNumber,
      'email': vendorEmail,
      'website': website,
      'notes': vendorNotes,
    };

    try {
      await patchVendor(id, values);
      handleCloseUser();
      getVendorInfo();
      setEditVendorErrors({});
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data) {
        setEditVendorErrors(error.response.data);
      }
    }
  }

  /** Make POST request to add new vehicle for a vendor */
  const addVehicle = async () => {
    // Combine 'Add Vehicle' form values to use in POST request
    let values = {
      'make': make,
      'model': model,
      'year': year,
      'color': color,
      'license': license,
      'vin': vin,
      'notes': notes,
    };

    // Try to create a new vehicle
    try {
      let url = `${process.env.REACT_APP_API_URL}vehicles/`;

      await axios.post(url, values, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(function (response) {
          console.log('New vehicle created:');
          console.log(response.data);

          console.log('Associating vehicle with vendor...');
          let user_id = vendorInfo.id;
          let new_vehicle_id = response.data.id;

          let vendor_vehicle_values = {
            'vendor': user_id,
            'vehicle': new_vehicle_id
          }
          let url2 = `${process.env.REACT_APP_API_URL}vendor-vehicle/`;

          // Associate vehicle with vendor
          axios.post(url2, vendor_vehicle_values, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });

          // Close modal
          handleCloseVehicle();

          // Refresh vendor info
          getVendorInfo();
        });
    } catch (error) {
      console.error(error);
    }
  }

  /** Make POST request to link existing vehicle to existing vendor */
  const linkVehicle = async () => {
    console.log('Attempting to link vehicle to vendor');
    let vehicleID = parseInt(vehicleLink.split(' ')[1].slice(0, -1));
    let vendorID = parseInt(vendorInfo.id);
    let values = {
      'vendor': vendorID,
      'vehicle': vehicleID
    };

    try {
      let url = `${process.env.REACT_APP_API_URL}vendor-vehicle/`;

      await axios.post(url, values)
        .then(function () {
          // Close modal
          handleCloseVehicleLink();

          // Refresh vendor info
          getVendorInfo();
        });
    } catch (error) {
      console.error(error);
    }
  }

  /** Make DELETE request to unlink vehicle to vendor */
  const unlinkVehicle = async () => {
    console.log('Attempting to unlink vehicle from vendor');

    let vendorID = parseInt(vendorInfo.id);
    let vehicleID = parseInt(vehicleLink.split(' ')[1].slice(0, -1));

    try {
      let url = `${process.env.REACT_APP_API_URL}vendor-vehicle/delete-by-filter/?vendor=${vendorID}&vehicle=${vehicleID}`;

      await axios.delete(url)
        .then(function () {
          // Close modal
          handleCloseVehicleUnlink();

          // Refresh vendor info
          getVendorInfo();
        });
    } catch (error) {
      console.error(error);
    }
  }

   /** Make GET request to get all vehicle data */
  const getVehicles = async () => {
    // Try to get all vehicles
    try {
      let url = `${process.env.REACT_APP_API_URL}vehicles/`;

      await axios.get(url)
        .then(function (response) {
          console.log('All vehicles');
          console.log(response.data);
          let rawVehicleList = response.data;
          console.log(rawVehicleList);
          setVehicles(rawVehicleList.map(vehicle => makeVehicleDescription(vehicle)));
        });
    } catch (error) {
      console.error(error);
    }
  }

  // Get vendor info
  useEffect(() => {
    getVendorInfo();
    getVehicles();
  }, [openVehicle, openVehicleLink, openVehicleUnlink])

  // Custom styles
    const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const style2 = {
    my: 5
  }

  return (
    <Box sx={{
      textAlign: 'center',
      minWidth: '519px',
      maxWidth: '75%',
      margin: 'auto',
      pb: 4,
    }}
    >
      <Typography variant='h2' sx={{ mb: 2 }}>Vendor Detail</Typography>
      <NavBar active='Vendors' />

      <Card sx={{
        maxWidth: 700,
        mx: 'auto',
        mt: 4,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        border: '1px solid #e0e0e0',
        backgroundColor: '#ffffff',
      }}>
        <CardContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant='h4' sx={{ fontWeight: 700, color: '#1f2937' }}>
              {vendorInfo.vendor_name || 'Vendor'}
              {vendorInfo.vendor_code ? ` (${vendorInfo.vendor_code})` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant='outlined' onClick={handleOpenUser}>Edit</Button>
              <Button variant='outlined' color='error' onClick={handleVendorDelete}>Delete</Button>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', pb: 1.5 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151' }}>Phone</Typography>
              <Typography sx={{ color: '#111827' }}>{vendorInfo.phone ? formatPhoneNum(vendorInfo.phone) : 'Not provided'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', pb: 1.5 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151' }}>Email</Typography>
              <Typography sx={{ color: '#111827' }}>{vendorInfo.email || 'Not provided'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', pb: 1.5 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151' }}>Website</Typography>
              {vendorInfo.website ? (
                <Typography
                  component='a'
                  href={vendorInfo.website.startsWith('http') ? vendorInfo.website : `https://${vendorInfo.website}`}
                  target='_blank'
                  rel='noreferrer'
                  sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'none' },
                  }}
                >
                  {vendorInfo.website}
                </Typography>
              ) : (
                <Typography sx={{ color: '#111827' }}>Not provided</Typography>
              )}
            </Box>
            <Box sx={{ display: 'block', pt: 0.5 }}>
              <Typography sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Notes</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap', color: '#4b5563', lineHeight: 1.6 }}>
                {vendorInfo.notes || 'No notes provided.'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Modal
        open={openUser}
        onClose={handleCloseUser}
        aria-labelledby="vendor-edit-modal-title"
        aria-describedby="vendor-edit-modal-description"
      >
        <Box sx={style}>
          <Typography id="vendor-edit-modal-title" variant="h6" component="h2">
            Edit Vendor
          </Typography>
          <FormGroup>
            <TextField
              required
              id='vendor-name'
              label='Vendor Name'
              variant='outlined'
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVendorErrors.vendor_name}
              helperText={editVendorErrors.vendor_name}
            />
            <TextField
              id='vendor-code'
              label='Vendor Code'
              variant='outlined'
              value={vendorCode}
              onChange={(e) => setVendorCode(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVendorErrors.vendor_code}
              helperText={editVendorErrors.vendor_code}
            />
            <TextField
              id='phone-number'
              label='Phone Number'
              variant='outlined'
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVendorErrors.phone}
              helperText={editVendorErrors.phone}
            />
            <TextField
              id='email'
              label='Email'
              variant='outlined'
              value={vendorEmail}
              onChange={(e) => setVendorEmail(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVendorErrors.email}
              helperText={editVendorErrors.email}
            />
            <TextField
              id='website'
              label='Website'
              variant='outlined'
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVendorErrors.website}
              helperText={editVendorErrors.website}
            />
            <TextField
              id='vendor-notes'
              label='Vendor Notes'
              variant='outlined'
              multiline
              rows={4}
              value={vendorNotes}
              onChange={(e) => setVendorNotes(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editVendorErrors.notes}
              helperText={editVendorErrors.notes}
            />
            <Button variant='contained' onClick={handleUpdateVendor}>Submit</Button>
          </FormGroup>
        </Box>
      </Modal>
    </Box>
  );
}